'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useT } from '@/lib/useT';
import PWAInstallButton from './PWAInstallButton';
import { APP_VERSION_LABEL } from '@/lib/version';

interface MobileAppsConfig {
  ios_app_store_url: string;
  android_play_store_url: string;
}

/**
 * Public footer — тот же язык, что у §15 homepage (`.lxh-ft`): графитовый
 * тёмный остров (`.on-dark`), mono-заголовки колонок, тонкая линия над
 * копирайт-строкой. Тёмный в обеих темах — как футер на homepage.
 */
export default function PublicFooter() {
  const t = useT();
  const [apps, setApps] = useState<MobileAppsConfig>({ ios_app_store_url: '', android_play_store_url: '' });

  useEffect(() => {
    const apiBase = process.env.NEXT_PUBLIC_API_URL || '/api';
    fetch(`${apiBase}/public/mobile-apps`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setApps(data); })
      .catch(() => {/* keep empty — buttons just won't render */});
  }, []);

  const hasIos = apps.ios_app_store_url.startsWith('https://');
  const hasAndroid = apps.android_play_store_url.startsWith('https://');

  // Цвета берутся из `.on-dark` (globals.css) — внутри футера legacy `--c-*`
  // переопределены на графитовые поверхности, поэтому здесь только имена.
  const linkBase: React.CSSProperties = { color: 'var(--c-text)', fontSize: '13.5px', transition: 'color 0.2s' };
  const colHeader: React.CSSProperties = {
    color: 'var(--c-muted)',
    fontFamily: 'var(--font-mono)',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    marginBottom: '14px',
  };

  return (
    <footer className="on-dark">
      <div className="px-6 md:px-8 pt-16 pb-7 max-w-[1440px] mx-auto">
        {/* Big animated wordmark — sweeping yellow accent over warm-beige stroke,
            ported from landing-v2. Standalone class in globals.css — no .lv2 wrapper. */}
        <div className="logixa-footer-big mb-10">LOGIXA.one</div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            {/* Вордмарк — тот же знак, что в шапке и на homepage */}
            <div className="mb-4"><span className="logixa-word">LOGI<i>X</i>A</span></div>
            <p className="text-[14px] leading-relaxed mb-5 max-w-[280px]" style={{ color: 'var(--c-text)' }}>{t('footer_tagline')}</p>
            {(hasIos || hasAndroid) && (
              <div className="flex flex-col gap-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--c-muted)', fontFamily: 'var(--font-mono)' }}>
                  {t('footer_get_app')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {hasIos && <AppStoreBadge href={apps.ios_app_store_url} t={t} />}
                  {hasAndroid && <PlayStoreBadge href={apps.android_play_store_url} t={t} />}
                </div>
              </div>
            )}
          </div>

          {/* Platform */}
          <div>
            <p style={colHeader}>{t('footer_col_platform')}</p>
            <ul className="space-y-2.5 text-[14px]">
              {[
                { href: 'https://logixa.one/platform/funktionen',    label: t('footer_link_features') },
                { href: 'https://logixa.one/pricing',                label: t('footer_link_pricing') },
                { href: 'https://logixa.one/platform/ki',            label: t('pub_dd_ai') },
                { href: 'https://logixa.one/platform/integrationen', label: t('pub_dd_integrations') },
                { href: 'https://logixa.one/#faq',                   label: t('faq_title') },
              ].map(l => (
                <li key={l.href}>
                  <a href={l.href} style={linkBase}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--c-heading)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--c-text)')}>
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p style={colHeader}>{t('footer_col_company')}</p>
            <ul className="space-y-2.5 text-[14px]">
              {[
                { href: 'https://logixa.one/about',       label: t('footer_link_about') },
                { href: 'https://logixa.one/companies',   label: t('pub_dd_companies') },
                { href: 'https://logixa.one/marketplace', label: t('mkt_index_badge') },
                { href: 'https://logixa.one/jobs',        label: t('jobs_badge') },
                { href: 'https://logixa.one/kontakt',     label: t('footer_contact') },
              ].map(l => (
                <li key={l.href}>
                  <Link href={l.href} style={linkBase}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--c-heading)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--c-text)')}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal + Install */}
          <div>
            <p style={colHeader}>{t('footer_col_legal')}</p>
            <ul className="space-y-2.5 text-[14px]">
              {[
                { href: 'https://logixa.one/impressum',   label: t('footer_imprint') },
                { href: 'https://logixa.one/datenschutz', label: t('footer_privacy') },
                { href: 'https://logixa.one/agb',         label: t('footer_terms') },
              ].map(l => (
                <li key={l.href}>
                  <Link href={l.href} style={linkBase}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--c-heading)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--c-text)')}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <button type="button"
                  onClick={() => { try { window.dispatchEvent(new Event('logixa-open-cookies')); } catch {} }}
                  style={{ ...linkBase, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--c-heading)')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--c-text)')}>
                  {t('cookie_title')}
                </button>
              </li>
              <PWAInstallButton variant="footer" />
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-4 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[13px]"
          style={{ borderTop: '1px solid var(--c-border)', color: 'var(--c-muted)' }}>
          <p>&copy; {new Date().getFullYear()} LOGIXA. {t('footer_rights')}</p>
          <p className="text-center">
            {t('footer_operated_by')} <strong style={{ color: 'var(--c-text)' }}>MATTALNET GRUPP OÜ</strong> · Tuukri tn 19-315, 10120 Tallinn, Estonia · VAT EE102519059
          </p>
          <p style={{ fontFamily: 'var(--font-mono)', opacity: 0.6 }}>LOGIXA Web {APP_VERSION_LABEL}</p>
        </div>
      </div>
    </footer>
  );
}

/* ───────── App store badges (kept verbatim — official-style pills) ───────── */
function AppStoreBadge({ href, t }: { href: string; t: (k: string) => string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={t('footer_app_store')}
      className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl transition-all hover:scale-[1.04]"
      style={{ background: 'linear-gradient(135deg, #1d1d1f 0%, #000 100%)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M16.365 1.43c0 1.14-.41 2.21-1.16 3.06-.91 1-2.39 1.79-3.83 1.68-.16-1.13.4-2.31 1.13-3.05.83-.86 2.27-1.5 3.86-1.69ZM20.5 17.62c-.54 1.25-.81 1.81-1.51 2.91-.97 1.55-2.34 3.49-4.04 3.5-1.51.02-1.9-.99-3.96-.97-2.06.01-2.49 1-4 .97-1.7-.03-3-1.78-3.97-3.32-2.71-4.34-3-9.43-1.32-12.13.94-1.51 2.42-2.39 3.81-2.39 1.42 0 2.31.78 3.49.78 1.14 0 1.83-.78 3.47-.78 1.24 0 2.55.68 3.48 1.85-3.06 1.68-2.56 6.05.55 7.58Z" />
      </svg>
      <div className="flex flex-col leading-tight text-left">
        <span className="text-[9px] tracking-wider uppercase opacity-80">{t('footer_app_store_top')}</span>
        <span className="text-sm font-semibold">{t('footer_app_store')}</span>
      </div>
    </a>
  );
}

function PlayStoreBadge({ href, t }: { href: string; t: (k: string) => string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={t('footer_play_store')}
      className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl transition-all hover:scale-[1.04]"
      style={{ background: 'linear-gradient(135deg, #1d1d1f 0%, #000 100%)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}>
      <svg width="20" height="22" viewBox="0 0 192 213" aria-hidden>
        <defs>
          <linearGradient id="pp-play-blue" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#00C3FF" /><stop offset="1" stopColor="#1A73E8" /></linearGradient>
          <linearGradient id="pp-play-red" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FF3A44" /><stop offset="1" stopColor="#C31162" /></linearGradient>
          <linearGradient id="pp-play-yellow" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FFE000" /><stop offset="1" stopColor="#FFBD00" /></linearGradient>
          <linearGradient id="pp-play-green" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#00F076" /><stop offset="1" stopColor="#00B97A" /></linearGradient>
        </defs>
        <path fill="url(#pp-play-blue)"   d="M3 3.7C1.1 5.6 0 8.5 0 12.4v188.2c0 3.9 1.1 6.8 3 8.7l.6.6 105.5-105.5v-2.5L3.6 3.1l-.6.6Z" />
        <path fill="url(#pp-play-yellow)" d="m144.2 139.4-35.1-35.1v-2.5l35.1-35.1 1 .5 41.6 23.6c11.9 6.7 11.9 17.7 0 24.5l-41.6 23.6-1 .5Z" />
        <path fill="url(#pp-play-red)"    d="M145.2 138.9 109 102.7 3 208.7c3.9 4.1 10.4 4.6 17.7.6l124.5-70.4Z" />
        <path fill="url(#pp-play-green)"  d="M145.2 66.5 20.7 -3.8c-7.3-4.1-13.8-3.6-17.7.5L109 102.7l36.2-36.2Z" />
      </svg>
      <div className="flex flex-col leading-tight text-left">
        <span className="text-[9px] tracking-wider uppercase opacity-80">{t('footer_play_store_top')}</span>
        <span className="text-sm font-semibold">{t('footer_play_store')}</span>
      </div>
    </a>
  );
}
